import { PROPERTY_DEFAULT_VALUE } from './enum.js';

/**
 * Convert Hex color into Decimal (copied from mojo.color)
 * @param {string} v The Hex color
 */
function encodeColor(v) {
	if (v === 'transparent') {
		return -1;
	}

	if (parseInt(v, 10) === -2) {
		return -2;
	}

	// remove prefix
	if (v.substring(0, 1) === '#') {
		v = v.substring(1);
	}

	// switch RR with BB and get the decimal value
	return parseInt(v.substring(4) + v.substring(2, 4) + v.substring(0, 2), 16);
}

/**
 * Convert decimal color into hexadecimal (copied from mojo.color)
 *
 * <pre>
 *   Here is the spec for color in CSS2
 *      EM { color: #f00 }              // #rgb
 *      EM { color: #ff0000 }           // #rrggbb
 *      EM { color: rgb(255,0,0) }      // integer range 0 - 255
 *      EM { color: rgb(100%, 0%, 0%) } // float range 0.0% - 100.0%
 *
 *   So we have this function to transform the color to #rrggbb style.
 *   current assumption is backend will give me a hexadecimal representation of the color
 *   in VB color in BBGGRR format in hexadecimal, and the value we get from backend is in decimal,
 *   so this function will convert decimal VB color to CSS2 color.
 *   anything wrong in converting, return black color "000000".
 * </pre>
 *
 * @param {int} color VB style color value in decimal.
 * @return {String} RGB color
 */
function decodeColor(color) {
	color = parseInt(color, 10);

	if (color === 0) {
		return PROPERTY_DEFAULT_VALUE.COLOR;
	}

	// get rgb color in decimal
	// eslint-disable-next-line no-bitwise
	const rgb =
		0x1000000 + (color & 0xff) * 0x10000 + (color & 0xff00) + (color & 0xff0000) / 0x10000;

	// convert to Hex string
	return `#${rgb.toString(16).substring(1).toUpperCase()}`;
}

/**
 * Takes in an int and converts it to a hex value
 *
 * @param {int} c - Int to be converted to hex
 */
function intToHex(c) {
	const hex = c.toString(16);
	return hex.length === 1 ? `0${hex}` : hex;
}

/**
 * Takes in specific rgb values and converts them to a hex value
 * @param {string | int} r - red value
 * @param {string | int} g - green value
 * @param {string | int} b - blue value
 */
function rgbToHex(r, g, b) {
	const rToHex = intToHex(parseInt(r, 10));
	const gToHex = intToHex(parseInt(g, 10));
	const bToHex = intToHex(parseInt(b, 10));

	return `#${rToHex}${gToHex}${bToHex}`;
}

/**
 * Converts a string with rgb to hex.
 * Example of expected input: `rgb(53, 56, 58)`
 * @param {string} rgb - Expects the string to come in the form of `rgb(r, g, b)`
 */
function stringRgbToHex(rgb) {
	// Getting rid of rgb(
	let parse = rgb.replace('rgb(', '');
	// Getting rid of ) so all that's left is 'r, g, b'
	parse = parse.replace(')', '');

	const [r, g, b] = parse.split(',');
	return rgbToHex(r, g, b);
}

/**
 * Converts a decimal value to a percentage value
 * @param {string | int} value - Value to be converted
 */
function decimalToPercentage(value) {
	const parsed = parseFloat(value);
	return parsed * 100;
}

/**
 * Converts a percentage value to a decimal value
 * @param {string | int} value - Value to be converted
 */
function percentageToDecimal(value) {
	const parsed = parseFloat(value);
	return parsed / 100;
}

/**
 * Converts a bool array of length 4 to a decimal value
 * @param {array} arr - Value to be converted
 */
function boolArrayToInt(arr) {
	const tmp = new Array(4);

	// convert bool arr to 0/1 array then make a str
	for (let i = 0; i < 4; i++) {
		tmp[i] = arr[i] ? 1 : 0;
	}

	const str = tmp.reverse().join('');

	return parseInt(str, 2); // convert binary str to decimal
}

/**
 * Converts a decimal value to bool array of length 4
 * @param {int} n - Value to be converted
 */
function intToBoolArray(n) {
	const arr = n.toString(2).split(''); // convert number to 0/1 array
	arr.length = 4; // set length of arr = 4
	return arr.map((v) => v === '1');
}

/**
 * Filters font family name from the font string
 * @param {string} font - CSS font style e.g. bold 3pt "Sans Serif"
 */
function getFontFamily(font) {
	return font.substring(font.lastIndexOf('pt') + 3).replace(/'/g, '');
}

// mstrmojo.string BEGIN

const xmlDecodeRep = {
	'&amp;': '&',
	'&lt;': '<',
	'&gt;': '>',
	'&#x09;': '\u0009', // tab
	'&#x0A;': '\n', // line feed
	'&#x0D;': '\r', // carriage return
	'&quot;': '"',
	// special handling DE137850
	'&#039;': "'"
};

function decodeXML(text) {
	const keys = [];
	// eslint-disable-next-line no-restricted-syntax, guard-for-in
	for (const k in xmlDecodeRep) {
		keys.push(k);
	}

	return text.replace(new RegExp(keys.join('|'), 'g'), ($0) => {
		const v = xmlDecodeRep[$0];
		return v.v || v;
	});
}

// mstrmojo.string END

// mstrmojo.ag.models.template.AgDataInterface BEGIN

function microchartStringToJson(gsi) {
	const { vp } = gsi;
	const chartCfg = vp && vp.chartCfg;
	let parsedData;

	if (chartCfg) {
		parsedData = JSON.parse(decodeXML(chartCfg));
	}

	return parsedData;
}

// mstrmojo.ag.models.template.AgDataInterface END

/**
 * This function will call the mojo code to get the available fonts and retun the items with the expected style also
 * This ensures that we have all the available fonts matching what we have in the old mojo base and styles applied correctly
 */
function getFontFamilyItems() {
	// eslint-disable-next-line
	const fontFamilyItems = mstrmojo._FormatDefinition.Families.slice(1).map(function (font) {
		const fontItem = { n: font.n };
		if (!font.symbolic) {
			fontItem.style = { fontFamily: font.n };
		}
		return fontItem;
	});

	return fontFamilyItems;
}
/**
 * This function will call the mojo code
 */
function getBasicColors() {
	const basicColors = mstrmojo.util.ui.ColorPicker.getBasicColors();
	return basicColors.map((color) =>
		mstrmojo.util.color.getCSSColorString(color.id).toUpperCase()
	);
}

/**
 * This function will call the mojo code
 */
function getPaletteColors() {
	const paletteColors = mstrApp && mstrApp.getPreferredColors();
	return paletteColors;
}
/**
 * This function will call the mojo code
 */
function addCustomColor(value) {
	if (!value || value === 'transparent') return; // user chose invalid state
	mstrmojo.util.ui.ColorPicker.addCustomColor(value);
}
/**
 * This function will call the mojo code
 */
function getCustomColors() {
	const customColors = mstrmojo.util.ui.ColorPicker.getCustomColors();
	return customColors.map((color) =>
		mstrmojo.util.color.getCSSColorString(color.id).toUpperCase()
	);
}

function getSixDigitHexString(color) {
	return mstrmojo.util.color.getCSSColorString(color);
}
export {
	encodeColor,
	decodeColor,
	stringRgbToHex,
	decimalToPercentage,
	percentageToDecimal,
	boolArrayToInt,
	intToBoolArray,
	getFontFamily,
	decodeXML,
	microchartStringToJson,
	getFontFamilyItems,
	getBasicColors,
	getCustomColors,
	getPaletteColors,
	addCustomColor,
	getSixDigitHexString
};
